import { RefObject, useCallback, DependencyList } from 'react';

import useEvent from './useEvent';
import { getFocusables } from '@pega/cosmos-react-core';

/**
 * @example useFocusTrap(elementRef)
 * @param elementRef ref of the element in which focus needs to be retained
 * @param addToDocument boolean value to specify if event to be attached to document or to be taken from elementRef, defaults to true
 * @param dependencies  list of variables or functions that will initiate this hook when they are updated.
 */

export const getActiveElement = (root: DocumentOrShadowRoot = document): Element | null => {
  let activeEl = root.activeElement;

  if (!activeEl) return null;

  if (activeEl.tagName.toLowerCase() === 'iframe' && (activeEl as HTMLIFrameElement).contentDocument)
    activeEl = getActiveElement((activeEl as HTMLIFrameElement).contentDocument!) ?? activeEl;

  if (activeEl.shadowRoot) activeEl = getActiveElement(activeEl.shadowRoot) ?? activeEl;

  return activeEl;
};

const useFocusTrap = (
  elementRef: RefObject<HTMLElement>,
  attachToDocument: boolean = true,
  dependencies: DependencyList = [],
) => {
  const onKeydown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Tab') {
      const focusables = getFocusables(elementRef);
      const { 0: firstFocusable, [focusables.length - 1]: lastFocusable } = focusables;
      const activeEl = getActiveElement();

      if (!e.shiftKey && (activeEl === elementRef.current || activeEl === lastFocusable)) {
        firstFocusable?.focus();
        e.preventDefault();
      } else if (e.shiftKey && (activeEl === elementRef.current || activeEl === firstFocusable)) {
        lastFocusable?.focus();
        e.preventDefault();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  useEvent('keydown', onKeydown, { target: attachToDocument ? undefined : elementRef });
};

export default useFocusTrap;
